import React, { useState, useEffect } from "react";
import axios from "axios";
import { MessageCircle, ArrowRight } from "lucide-react";
import ChatWidget from "./ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_META = {
  dental: {
    badge: "Healthcare",
    business: "Brightsmile Dental",
    headline: "A booking & FAQ bot for a busy dental clinic.",
    description:
      "Books appointments, answers insurance questions, and handles office-hours queries — so the front desk can focus on patients in the chair.",
    metric: "Captures leads 24/7",
    accent: "#7BAE9F",
  },
  realestate: {
    badge: "Real Estate",
    business: "Summit Realty",
    headline: "A lead-qualifier for a boutique brokerage.",
    description:
      "Pre-qualifies buyers and sellers, surfaces featured listings, and routes hot leads to the right agent within hours — not days.",
    metric: "Qualifies leads in 60 sec",
    accent: "#C9A961",
  },
  ecommerce: {
    badge: "E-Commerce",
    business: "Velvet & Vine",
    headline: "A style assistant for an online fashion boutique.",
    description:
      "Helps shoppers find the perfect piece, checks order status, and recovers abandoned carts with a real conversation, not a popup.",
    metric: "Recovers 18% more carts",
    accent: "#D49B9B",
  },
};

export default function ChatbotDemos() {
  const [personas, setPersonas] = useState([]);
  const [activePersona, setActivePersona] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/personas`)
      .then((res) => setPersonas(res.data))
      .catch((err) => console.error("Failed to load personas", err));
  }, []);

  return (
    <section id="demos" data-testid="demos-section" className="bg-cream section-pad">
      <div className="container-edit">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5">Live Demos</div>
            <h2 className="h-section text-plum-deepest">
              Real chatbots. <span className="font-display-italic text-gold-deep">Real AI.</span>
              <br />
              Try them yourself.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-muted-plum leading-relaxed text-lg font-body">
              Each demo below is a fully functioning AI assistant I built — powered by the
              same tech that will power yours. Click any card to start chatting. No signup, no
              tricks. Just proof.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {personas.map((p) => {
            const meta = DEMO_META[p.id];
            return (
              <article
                key={p.id}
                data-testid={`demo-card-${p.id}`}
                className="card-soft cursor-pointer flex flex-col"
                onClick={() => setActivePersona(p)}
              >
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="inline-block text-[0.7rem] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full"
                    style={{
                      backgroundColor: `${meta.accent}22`,
                      color: meta.accent,
                    }}
                  >
                    {meta.badge}
                  </span>
                  <MessageCircle size={20} className="text-plum/30" />
                </div>

                <div className="font-display-italic text-gold-deep text-sm mb-2">
                  {meta.business}
                </div>
                <h3 className="h-card text-plum-deepest mb-4 leading-snug">
                  {meta.headline}
                </h3>
                <p className="text-sm text-muted-plum leading-relaxed mb-6 flex-1">
                  {meta.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-plum/10">
                  <span className="text-xs uppercase tracking-wider text-gold-deep font-semibold">
                    {meta.metric}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-plum text-sm font-semibold group-hover:gap-2 transition-all">
                    Try it
                    <ArrowRight size={15} />
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <p className="font-display-italic text-2xl text-plum-deepest mb-2">
            "These are sample businesses — yours could be next."
          </p>
          <a href="#contact" className="btn-primary mt-4">
            Build mine
            <ArrowRight size={18} />
          </a>
        </div>
      </div>

      {activePersona && (
        <ChatWidget persona={activePersona} onClose={() => setActivePersona(null)} />
      )}
    </section>
  );
}
